import React from 'react';
import Layout from '../components/Layout/Layout';
import { getActiveSeason } from '../config/seasons';
import { REFUND_POLICY, REFUND_CONTACT } from '../shared/refundPolicy';
import { SeasonAtAGlance, JumpLinks, InlineLink, formatDay } from '../components/Season/SeasonFacts';

const SECTIONS = [
  ['glance', 'At a glance'],
  ['who', 'Who can enter'],
  ['solos', 'Solos'],
  ['ensembles', 'Ensembles'],
  ['shoes', 'Pointe or soft shoes'],
  ['music', 'Music & changes'],
  ['fees', 'Fees'],
  ['refunds', 'Refunds'],
  ['repertoire', 'Repertoire list'],
];

const Section = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: '2.75rem', scrollMarginTop: '90px' }}>
    <h2 style={{ textAlign: 'left' }}>{title}</h2>
    {children}
  </section>
);

const Table = ({ head, rows }) => (
  // Rounded like the site's cards; overflow clips the header's corners and
  // still lets a wide table scroll sideways on a phone.
  <div style={{
    overflowX: 'auto', margin: '1rem 0', borderRadius: '6px',
    border: '1px solid #eee', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
  }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: '0.98rem' }}>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} style={{ textAlign: 'left', padding: '0.65rem 0.8rem', background: 'var(--primary-gold)', color: 'white' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          <tr key={row[0]}>
            {row.map((cell, i) => (
              <td key={i} style={{
                padding: '0.65rem 0.8rem', verticalAlign: 'top', fontWeight: i === 0 ? 600 : 400,
                borderBottom: r < rows.length - 1 ? '1px solid #eee' : 'none',
              }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CompetitionRules = () => {
  const season = getActiveSeason();
  const { year } = season;
  const p = season.prices;

  return (
    <Layout
      title={`${year} Rules & Fees | Prix du Canada Ballet Competition`}
      description={`Prix du Canada ${year} rules and fees: who can enter, solo and ensemble divisions, time limits, pointe rules, music, prices and refunds.`}
      keywords="ballet competition rules Canada, ballet competition Toronto registration, adult ballet competition requirements, ballet ensemble competition rules, classical ballet repertoire"
    >
      <section className="elegant-hero">
        <div className="container">
          <h1>{year} Rules &amp; Fees</h1>
          <p style={{ textAlign: 'center' }}>Everything you need to know before you register.</p>
        </div>
      </section>

      <JumpLinks items={SECTIONS} />

      <section className="section" style={{ background: 'var(--soft-cream)', paddingTop: '2.5rem' }}>
        <div className="container" style={{ maxWidth: '900px', textAlign: 'left' }}>
          <div style={{ marginBottom: '2.75rem', scrollMarginTop: '90px' }} id="glance">
            <SeasonAtAGlance season={season} />
          </div>

          <Section id="who" title="Who can enter">
            <ul style={{ marginLeft: '1.2em' }}>
              <li>Dancers <strong>7 years and older</strong>, of any gender, from anywhere in the world.</li>
              <li>Your age is your age on <strong>January 1, {year}</strong>.</li>
              <li>Professional dancers can't compete.</li>
              <li>All entries must be <strong>classical ballet</strong> (see the <a href="#repertoire" style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>repertoire list</a>). Contemporary, jazz, lyrical, hip-hop and musical theatre are not allowed.</li>
            </ul>
          </Section>

          <Section id="solos" title="Solos">
            <ul style={{ marginLeft: '1.2em' }}>
              <li>You can enter <strong>up to 2 solos</strong>.</li>
              <li>Each solo can be <strong>up to 3 minutes</strong>. You can't buy extra time. Going over can lose points or be disqualified.</li>
              <li>Skip the révérence and keep your entrance and exit short.</li>
            </ul>
            <h3 style={{ textAlign: 'left', marginTop: '1.5rem' }}>Solo divisions</h3>
            <Table
              head={['Division', 'Who']}
              rows={[
                ['Ages 7–9', 'Dancers aged 7 to 9'],
                ['Ages 10–11', 'Dancers aged 10 to 11'],
                ['Ages 12–14', 'Dancers aged 12 to 14'],
                ['Ages 15–17', 'Dancers aged 15 to 17'],
                ['Adult Competitive', 'Adults 18+ with advanced training'],
                ['Adult Recreational – Pointe', 'Adults 18+ who dance for fun, on pointe'],
                ['Adult Recreational – Soft Shoes', 'Adults 18+ who dance for fun, in soft shoes'],
              ]}
            />
            <p style={{ fontSize: '0.95rem', color: 'var(--soft-gray)' }}>
              Recreational is for adults only. Dancers 17 and under all compete in their age division.
            </p>
          </Section>

          <Section id="ensembles" title="Ensembles (new!)">
            <p>An ensemble is a group of <strong>3 to 8 dancers</strong> who perform one piece together.</p>
            <ul style={{ marginLeft: '1.2em' }}>
              <li>Each dancer can be in <strong>up to 2 ensembles</strong>, and can also enter up to 2 solos.</li>
              <li>Dancers can come from <strong>different schools</strong>.</li>
              <li>Each dancer registers and pays for their own spot.</li>
            </ul>

            <h3 style={{ textAlign: 'left', marginTop: '1.5rem' }}>Time limit</h3>
            <Table
              head={['Group size', 'Longest piece']}
              rows={[
                ['3–5 dancers', '3 minutes'],
                ['6–8 dancers', '4 minutes'],
              ]}
            />
            <p>
              Need more time? You can add <strong>up to 1 extra minute for $50</strong> per ensemble.
              Email us to arrange it. Pieces that go over the time limit may be stopped.
            </p>

            <h3 style={{ textAlign: 'left', marginTop: '1.5rem' }}>Ensemble divisions</h3>
            <p>Your group's division depends on the <strong>average age</strong> of its dancers (ages on January 1, {year}).</p>
            <Table
              head={['Division', 'Average age', 'Who can be in it']}
              rows={[
                ['Junior', '7–11', 'Every dancer 7 to 17'],
                ['Intermediate / Senior', '12–17', 'Every dancer 17 or younger'],
                ['Adult', '18+', 'Every dancer 18 or older'],
              ]}
            />
            <ul style={{ marginLeft: '1.2em' }}>
              <li><strong>Youth and adults can't dance in the same ensemble.</strong></li>
              <li>
                In youth ensembles (Junior and Intermediate/Senior), every dancer must be within
                <strong> 4 years</strong> of the group's average age.
                <br />
                <span style={{ color: 'var(--soft-gray)' }}>
                  Example: ages 10, 10, 11, 12 and 13 have an average of 11.2, so this group is OK.
                  Ages 7, 12, 13, 13 and 17 have an average of 12.4. The 7-year-old is 5.4 years
                  younger, so this group can't compete.
                </span>
              </li>
              <li>Don't worry about the math: the website checks your group for you while you build it.</li>
              <li>We may combine divisions after registration closes, depending on how many groups enter.</li>
            </ul>

            <h3 style={{ textAlign: 'left', marginTop: '1.5rem' }}>How to register a team</h3>
            <ol style={{ marginLeft: '1.2em' }}>
              <li>Each dancer registers on their own account and adds an ensemble.</li>
              <li>One dancer chooses <strong>Start a team</strong>. They get a team code (like <code>PDC-7KQ3</code>) to share.</li>
              <li>Everyone else chooses <strong>Join with a code</strong>.</li>
              <li>Not sure about your team yet? Choose <strong>Join later</strong>. You can pay now and join a team before registration closes.</li>
            </ol>
            <p>Teachers registering a whole school build their ensembles in the studio portal instead.</p>
            <p>
              Ensembles must be classical ballet: classical repertoire, adapted repertoire, or new choreography
              based on classical technique. Choreography protected by copyright (for example, works by
              George Balanchine) is not allowed.
            </p>
          </Section>

          <Section id="shoes" title="Pointe or soft shoes">
            <p>When you register, you choose <strong>pointe or soft shoes</strong> for each solo and ensemble. You can change it until registration closes.</p>
            <Table
              head={['Age', 'Rule']}
              rows={[
                ['10 and under', 'Soft shoes only. Pointe is not allowed.'],
                ['11', 'Soft shoes recommended. Pointe is strongly discouraged.'],
                ['12 to 17', 'Pointe shoes are expected. Judges will take off technique points for soft shoes.'],
                ['Adults 18+', 'Pointe or soft shoes, your choice. Adult Recreational has a separate division for each. In Adult Competitive, pointe work may earn extra points.'],
              ]}
            />
            <p style={{ fontSize: '0.95rem', color: 'var(--soft-gray)' }}>
              Not sure if you're ready for pointe? Ask your teacher. Your safety comes first.
            </p>
          </Section>

          <Section id="music" title="Music & changes">
            <ul style={{ marginLeft: '1.2em' }}>
              <li>Upload your music in your registration <strong>before registration closes</strong> (about 2 weeks before competition day).</li>
              <li>Changes to your variation or music after registration closes cost <strong>$20</strong>.</li>
              <li>Any change to an ensemble after it's finalized (for example, replacing an injured dancer) must go through us. Email <a href="mailto:info@prixducanada.com" style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>info@prixducanada.com</a>.</li>
            </ul>
          </Section>

          <Section id="fees" title={`${year} fees`}>
            {p ? (
              <>
                <p>You pay a <strong>registration fee once</strong>, plus a fee for <strong>each entry</strong>.</p>
                <Table
                  head={['What', 'Price (CAD)']}
                  rows={[
                    ['Registration fee', season.earlyBirdEndsAt
                      ? `$${p.registration} ($${p.registrationEarlyBird} if you pay by ${formatDay(season.earlyBirdEndsAt)})`
                      : `$${p.registration}`],
                    ['Each solo', `$${p.solo}`],
                    ['Each ensemble', `$${p.ensemble} per dancer`],
                    ...(season.comboEndsAt
                      ? [['Solo + ensemble deal', `$${p.combo} instead of $${p.solo + p.ensemble}, until ${formatDay(season.comboEndsAt)}`]]
                      : []),
                    ['Performance video', 'Free'],
                    ['Photos', 'Buy on competition day'],
                  ]}
                />
                <p>13% HST is added at checkout.</p>
                <p>
                  <strong>Example:</strong> one solo with the early-bird price is ${p.registrationEarlyBird} + ${p.solo} = ${p.registrationEarlyBird + p.solo} + HST.
                </p>
                <p>
                  <strong>Add more later:</strong> you can come back and add a solo or ensemble any time before registration
                  closes. You only pay for what's new.
                </p>
                {season.schoolPricing && (
                  <>
                    <h3 style={{ textAlign: 'left', marginTop: '1.5rem' }}>Schools and studios</h3>
                    <p>Schools pay the regular prices, with a discount on the whole order:</p>
                    <Table
                      head={['Order total (before tax)', 'Discount']}
                      rows={season.schoolPricing.tiers.map((t) => [`$${t.from.toLocaleString('en-CA')} or more`, `${t.percentOff}% off`])}
                    />
                    <p>Schools don't pay online. We send an invoice.</p>
                  </>
                )}
              </>
            ) : (
              <p>Fees will be posted when registration opens.</p>
            )}
          </Section>

          <Section id="refunds" title="Refunds">
            <Table head={['What', 'Refund']} rows={REFUND_POLICY.map((r) => [r.item, r.policy])} />
            <p>{REFUND_CONTACT}</p>
          </Section>

          <p style={{ marginBottom: '2.75rem' }}>
            Still have questions? Check the <InlineLink to="/faq">FAQ</InlineLink>.
          </p>

          <section id="repertoire" style={{ scrollMarginTop: '90px' }}>
            <h2 style={{textAlign: 'left'}}>Repertoire list</h2>
            <p style={{textAlign: 'left'}}>We strongly suggest choosing a solo from this list. If you want to dance something else, save it in your registration and email us. Our judges will check that it suits your age and level.</p>
            
            <div style={{marginBottom: '3rem'}}>
              <h3 style={{color: 'var(--primary-gold)', marginBottom: '1rem', textAlign: 'left'}}>Age 7-11</h3>
              <ul style={{marginLeft: '1.2em', columns: '2', columnGap: '2rem'}}>
                <li>Don Quixote – Kitri Act III Variation</li>
                <li>Don Quixote – Cupid Variation</li>
                <li>Giselle – Peasant Variation</li>
                <li>La Esmeralda – Tambourine Variation</li>
                <li>Paquita – Any Solo Variation (excluding Étoile)</li>
                <li>Raymonda – Act I Variation (Pizzicato Variation)</li>
                <li>Raymonda – Act I Variation (Scarf Variation)</li>
                <li>Sleeping Beauty – Lilac Fairy Variation</li>
                <li>Sleeping Beauty – The Fairy of Grace (Fairy of the Flowing Stream)</li>
                <li>Sleeping Beauty – Fairy of Joy (Singing Canary Fairy)</li>
                <li>Sleeping Beauty – The Fairy of Generosity (Breadcrumb Fairy)</li>
                <li>Sleeping Beauty – The Fairy of Temperament (Flickering Flame Fairy)</li>
                <li>Sleeping Beauty – The Fairy of Curiosity (Golden Vine Fairy)</li>
                <li>Sleeping Beauty – Any Solo Variation from Act I Fairies</li>
                <li>Sleeping Beauty – Bluebird Female Variation</li>
                <li>Swan Lake – Pas de Trois 1st Girl</li>
                <li>Swan Lake – Pas de Trois 3rd Girl</li>
                <li>The Awakening of Flora – Flora Variation</li>
                <li>Diana & Actéon – Diana Variation</li>
                <li>Harlequinade – Columbine Variation</li>
                <li>The Nutcracker – Sugar Plum Fairy Variation</li>
                <li>Fairy Doll – Doll Solo Variation</li>
                <li>La Fille Mal Gardée – Lise Variation</li>
                <li>Coppélia – Swanhilda Act I Variation</li>
                <li>Coppélia – Swanhilda Act III Variation</li>
                <li>Flower Festival at Genzano – Female Variation</li>
                <li>Satanella – Female Solo Variation</li>
                <li>Walpurgis Nacht – Female Solo Variation</li>
                <li>Talisman Variation</li>
                <li>Graduation Ball Variation</li>
                <li>Flames Of Paris Variation</li>
              </ul>
            </div>

            <div>
              <h3 style={{color: 'var(--primary-gold)', marginBottom: '1rem', textAlign: 'left'}}>Age 12+ (including adults)</h3>
              <ul style={{marginLeft: '1.2em', columns: '2', columnGap: '2rem'}}>
                <li>Don Quixote – Kitri Act I Variation</li>
                <li>Don Quixote – Kitri Act III Variation</li>
                <li>Don Quixote – Cupid Variation</li>
                <li>Don Quixote – Dulcinea Variation</li>
                <li>Don Quixote – The Queen of the Dryads</li>
                <li>Giselle – Giselle Act I Variation</li>
                <li>Giselle – Peasant Variation</li>
                <li>La Bayadère – Gamzatti Variation</li>
                <li>La Bayadère – Nikiya Variation (under 3:00)</li>
                <li>La Bayadère – Shade Variation</li>
                <li>La Esmeralda – Tambourine Variation</li>
                <li>Le Corsaire – Odalisque Variation 1</li>
                <li>Le Corsaire – Odalisque Variation 2</li>
                <li>Le Corsaire – Odalisque Variation 3</li>
                <li>Le Corsaire – Medora Variation</li>
                <li>Paquita – Any Solo Variation (including Étoile)</li>
                <li>Raymonda – Dream Variation</li>
                <li>Raymonda – Act I Variation (Pizzicato Variation)</li>
                <li>Raymonda – Act I Variation (Scarf Variation)</li>
                <li>Sleeping Beauty – Aurora Act I Variation</li>
                <li>Sleeping Beauty – Aurora Act II Variation</li>
                <li>Sleeping Beauty – Lilac Fairy Variation</li>
                <li>Sleeping Beauty – The Fairy of Grace (Fairy of the Flowing Stream)</li>
                <li>Sleeping Beauty – Fairy of Joy (Singing Canary Fairy)</li>
                <li>Sleeping Beauty – The Fairy of Generosity (Breadcrumb Fairy)</li>
                <li>Sleeping Beauty – The Fairy of Temperament (Flickering Flame Fairy)</li>
                <li>Sleeping Beauty – The Fairy of Curiosity (Golden Vine Fairy)</li>
                <li>Sleeping Beauty – Bluebird Female Variation</li>
                <li>Swan Lake – Odette Variation</li>
                <li>Swan Lake – Odile (Black Swan) Variation</li>
                <li>Swan Lake – Pas de Trois 1st Girl</li>
                <li>Swan Lake – Pas de Trois 3rd Girl</li>
                <li>The Awakening of Flora – Flora Variation</li>
                <li>Diana & Actéon – Diana Variation</li>
                <li>Harlequinade – Columbine Variation</li>
                <li>The Nutcracker – Sugar Plum Fairy Variation</li>
                <li>Fairy Doll – Doll Solo Variation</li>
                <li>La Fille Mal Gardée – Lise Variation</li>
                <li>Coppélia – Swanhilda Act I Variation</li>
                <li>Coppélia – Swanhilda Act III Variation</li>
                <li>Flower Festival at Genzano – Female Variation</li>
                <li>La Sylphide – Sylph Variation</li>
                <li>Satanella – Female Solo Variation</li>
                <li>Grand Pas Classique – Female Solo Variation</li>
                <li>Walpurgis Nacht – Female Solo Variation</li>
                <li>Talisman Variation</li>
                <li>Graduation Ball Variation</li>
                <li>Flames Of Paris Variation</li>
              </ul>
            </div>
          </section>
        </div>
      </section>
    </Layout>
  );
};

export default CompetitionRules;
